import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

import { LOCALISATION_CONFIG } from "@/shared/config/localisation";
import type { RootState } from "@/shared/redux/store/store";

import { CAR_QUOTE_STEP_KEYS, CarQuoteStepKey } from "../../common/static";
import {
  CarAdditionalInfoValues,
  CarDocumentUploadValues,
  CarDriverFormValues,
  CarlDocumentUploadFieldKey,
  CarPaymentValues,
  CarPersonalFormValues,
  CarQuoteFormState,
  CarQuoteSelectionValues,
  CarQuoteState,
  CarUploadedDocumentMap,
  CarVehicleFormValues,
  SaveCarQuoteStepPayload,
} from "../type";

export function createSaveCarQuoteStepPayload<TStepKey extends CarQuoteStepKey>(
  stepName: TStepKey,
  data: CarQuoteFormState[TStepKey]
): Extract<SaveCarQuoteStepPayload, { stepName: TStepKey }> {
  return { stepName, data } as Extract<SaveCarQuoteStepPayload, { stepName: TStepKey }>;
}

export function saveCarQuoteStepFor<TStepKey extends CarQuoteStepKey>(
  stepName: TStepKey,
  data: CarQuoteFormState[TStepKey]
) {
  return saveCarQuoteStep(createSaveCarQuoteStepPayload(stepName, data));
}

export const defaultVehicleFormValues: CarVehicleFormValues = {
  carModel: "",
  modelYear: "",
  vehicleVariant: "",
  gccSpec: false,
  isNewCar: false,
  registeredEmirate: "",
  registrationDate: "",
  registeredUnder: "Individual",
  carValue: "",
};

export const defaultPersonalFormValues: CarPersonalFormValues = {
  firstName: "",
  lastName: "",
  mobileNumber: {
    countryCode: LOCALISATION_CONFIG.mobileCountryCode,
    countryCodeIso: LOCALISATION_CONFIG.mobileCountryIso,
    number: "",
  },
  email: "",
  partnerCode: "",
  friendsAndFamilyContact: "",
  marketingConsent: "no",
  vehicleRegistrationNumber: "",
};

export const defaultDriverFormValues: CarDriverFormValues = {
  dateOfBirth: "",
  nationality: "",
  gender: "",
  uaeDrivingExperience: "",
  outsideUaeDrivingExperience: "",
  hasActivePolicy: false,
  currentPolicyType: "",
  currentInsurer: "",
  claimsLastYear: false,
  no_claims_certificate: "",
};

export const defaultQuoteSelectionValues: CarQuoteSelectionValues = {
  selectedQuoteId: "",
};

export const defaultDocumentUploadValues: CarDocumentUploadValues = {
  emiratesIdFront: false,
  emiratesIdBack: false,
  emiratesIdCombined: false,
  aadhaarFront: false,
  aadhaarBack: false,
  aadhaarCombined: false,
  drivingLicenseFront: false,
  drivingLicenseBack: false,
  drivingLicenseCombined: false,
  confirmDocuments: false,
  vehicleLicense: false,
  panCard: false,
  previousPolicy: false,
  noClaimProof: false,
  otherDocument: false,
};

export const defaultAdditionalInfoValues: CarAdditionalInfoValues = {
  // Indian motor policy fields
  residentialAddress: "",
  panNumber: "",
  drivingLicenceNumber: "",
  drivingLicenceExpiryDate: "",
  vehicleRegistrationNumber: "",
  chassisNumber: "",
  engineNumber: "",
  isVehicleUnderLoan: "",
  previousInsurerName: "",
  previousPolicyNumber: "",
  previousPolicyExpiryDate: "",
  ncbPercentage: "",
  communicationAddressSame: "",
  communicationAddress: "",
  isCngLpgKitInstalled: "",
  nomineeFullName: "",
  nomineeRelationship: "",
  nomineeDateOfBirth: "",
  // UAE motor policy fields
  estimatedAnnualMileage: "",
  emiratesId: "",
  carFinancedBy: "",
  vehicleColor: "",
  tcfNumber: "",
  registrationType: "",
  plateCode: "",
  plateNumber: "",
  authorizationAccepted: false,
};

export const defaultPaymentValues: CarPaymentValues = {
  paymentMethod: "Card",
  billingEmail: "",
  cardHolder: "",
  cardNumber: "",
  expiry: "",
  cvv: "",
  confirmPayment: false,
};

export const defaultCarQuoteFormState: CarQuoteState = {
  documentUploadDocuments: {},
  [CAR_QUOTE_STEP_KEYS.VEHICLE_DETAILS]: defaultVehicleFormValues,
  [CAR_QUOTE_STEP_KEYS.PERSONAL_DETAILS]: defaultPersonalFormValues,
  [CAR_QUOTE_STEP_KEYS.DRIVER_DETAILS]: defaultDriverFormValues,
  [CAR_QUOTE_STEP_KEYS.QUOTE_SELECTION]: defaultQuoteSelectionValues,
  [CAR_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD]: defaultDocumentUploadValues,
  [CAR_QUOTE_STEP_KEYS.ADDITIONAL_INFO]: defaultAdditionalInfoValues,
  [CAR_QUOTE_STEP_KEYS.PAYMENT]: defaultPaymentValues,
};

const carQuoteSlice = createSlice({
  name: "carQuote",
  initialState: defaultCarQuoteFormState,
  reducers: {
    saveCarQuoteStep(state, action: PayloadAction<SaveCarQuoteStepPayload>) {
      switch (action.payload.stepName) {
        case CAR_QUOTE_STEP_KEYS.PERSONAL_DETAILS:
          state[CAR_QUOTE_STEP_KEYS.PERSONAL_DETAILS] = action.payload.data;
          break;
        case CAR_QUOTE_STEP_KEYS.VEHICLE_DETAILS:
          state[CAR_QUOTE_STEP_KEYS.VEHICLE_DETAILS] = action.payload.data;
          break;
        case CAR_QUOTE_STEP_KEYS.DRIVER_DETAILS:
          state[CAR_QUOTE_STEP_KEYS.DRIVER_DETAILS] = action.payload.data;
          break;
        case CAR_QUOTE_STEP_KEYS.QUOTE_SELECTION:
          state[CAR_QUOTE_STEP_KEYS.QUOTE_SELECTION] = action.payload.data;
          break;
        case CAR_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD:
          state[CAR_QUOTE_STEP_KEYS.DOCUMENT_UPLOAD] = action.payload.data;
          break;
        case CAR_QUOTE_STEP_KEYS.ADDITIONAL_INFO:
          state[CAR_QUOTE_STEP_KEYS.ADDITIONAL_INFO] = action.payload.data;
          break;
        case CAR_QUOTE_STEP_KEYS.PAYMENT:
          state[CAR_QUOTE_STEP_KEYS.PAYMENT] = action.payload.data;
          break;
      }
    },
    resetCarQuoteState() {
      return defaultCarQuoteFormState;
    },
    saveCarUploadDocuments(
      state,
      action: PayloadAction<{
        fieldKey: CarlDocumentUploadFieldKey;
        document: CarUploadedDocumentMap[CarlDocumentUploadFieldKey];
      }>
    ) {
      const { fieldKey, document } = action.payload;
      if (!document) {
        delete state.documentUploadDocuments[fieldKey];
        return;
      }
      state.documentUploadDocuments[fieldKey] = document;
    },
  },
});

export const { resetCarQuoteState, saveCarQuoteStep, saveCarUploadDocuments } =
  carQuoteSlice.actions;

export const selectCarQuoteState = (state: RootState) => state.carQuote;
export const selectCarUploadedDocuments = (state: RootState) =>
  state.carQuote.documentUploadDocuments;

export const selectCarQuoteStepData =
  <TStepKey extends CarQuoteStepKey>(stepName: TStepKey) =>
  (state: RootState) =>
    state.carQuote[stepName];

export default carQuoteSlice.reducer;
